import { Injectable, inject } from '@angular/core';
import { AITool } from '../tool.decorator';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { ContactOfflineService } from '@omni/services/contact/contact.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { CallPlanOfflineService } from '@omni/services/call-plan/call-plan.offline.service';
import { PresentationService } from '@omni/services/presentation/presentation.service';
import { ResourceService } from '@omni/services/resource/resource.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';

/**
 * Pure "find me X" lookups across the local in-memory caches and (for call
 * plans) the on-disk PouchDB snapshot. None of these mutate state — they read,
 * filter, and project into the shapes the model wants to see.
 */
@Injectable({ providedIn: 'root' })
export class SearchTools {
  private readonly accountService = inject(AccountOfflineService);
  private readonly contactService = inject(ContactOfflineService);
  private readonly activityService = inject(ActivityService);
  private readonly callPlanService = inject(CallPlanOfflineService);
  private readonly presentationService = inject(PresentationService);
  private readonly resourceService = inject(ResourceService);
  private readonly diskService = inject(DiskService);

  @AITool('search_accounts', 'Search for customer accounts/organizations by name or other criteria', [
    { name: 'search_term', type: 'string', description: 'The search term to find accounts', required: true },
    { name: 'limit', type: 'number', description: 'Maximum number of results to return', required: false, default: 10 },
  ])
  async searchAccounts(search_term: string, limit = 10): Promise<any> {
    try {
      const accountList = this.accountService.accounts || [];
      const filtered = accountList.filter(acc =>
        acc.accountName?.toLowerCase().includes(search_term.toLowerCase()),
      );
      const limitedResults = filtered.slice(0, limit);

      return {
        success: true,
        count: limitedResults.length,
        accounts: limitedResults.map(acc => ({
          id: acc.id,
          name: acc.accountName,
          type: acc.accountLevel,
          city: acc.city,
        })),
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Search failed' };
    }
  }

  @AITool('search_contacts', 'Search for contacts (people/HCPs) by name or other criteria', [
    { name: 'search_term', type: 'string', description: 'The search term to find contacts', required: true },
    { name: 'limit', type: 'number', description: 'Maximum number of results to return', required: false, default: 10 },
  ])
  async searchContacts(search_term: string, limit = 10): Promise<any> {
    try {
      const contactList = this.contactService.contacts || [];
      const term = search_term.toLowerCase();
      const filtered = contactList.filter(contact =>
        contact.fullName?.toLowerCase().includes(term) ||
        contact.lastName?.toLowerCase().includes(term),
      );
      const limitedResults = filtered.slice(0, limit);

      return {
        success: true,
        count: limitedResults.length,
        contacts: limitedResults.map(contact => ({
          id: contact.ID,
          name: (contact as any).fullName || (contact as any).name,
          title: (contact as any).jobTitle || (contact as any).title,
          account: (contact as any).accountName || (contact as any).account,
        })),
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Search failed' };
    }
  }

  @AITool('search_activities', 'Search for activities (meetings, calls, appointments) by subject or other criteria', [
    { name: 'search_term', type: 'string', description: 'The search term to find activities', required: true },
    { name: 'limit', type: 'number', description: 'Maximum number of results to return', required: false, default: 10 },
  ])
  async searchActivities(search_term: string, limit = 10): Promise<any> {
    try {
      const results = this.activityService.activities;
      const activities = Array.isArray(results) ? results : [];
      const filtered = activities.filter(a =>
        a.subject?.toLowerCase().includes(search_term.toLowerCase()),
      );
      const limitedResults = filtered.slice(0, limit);

      return {
        success: true,
        count: limitedResults.length,
        activities: limitedResults.map(activity => ({
          id: activity.ID,
          subject: activity.subject,
          type: (activity as any).activityTypeName,
          date: activity.scheduledStart,
        })),
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Search failed' };
    }
  }

  @AITool('search_call_plans', 'Search for call plans by customer name, product, or other criteria', [
    {
      name: 'search_term',
      type: 'string',
      description: 'The search term to find call plans (e.g., customer name, product)',
      required: true,
    },
    { name: 'limit', type: 'number', description: 'Maximum number of results to return', required: false, default: 10 },
  ])
  async searchCallPlans(search_term: string, limit = 10): Promise<any> {
    try {
      const callPlansDoc = await this.diskService.retrieve(DB_KEY_PREFIXES.MY_POSITON_CALL_PLANS);

      if (!callPlansDoc || !callPlansDoc.raw || callPlansDoc.raw.length === 0) {
        return {
          success: true,
          message: 'No call plans available to search',
          callPlans: [],
        };
      }

      const allPlans = this.callPlanService.aggregateRepCallPlans(callPlansDoc.raw);

      const searchLower = search_term.toLowerCase();
      const matchedPlans = allPlans.filter(plan =>
        plan.customerName?.toLowerCase().includes(searchLower) ||
        plan.cycleplanname?.toLowerCase().includes(searchLower) ||
        plan.productName?.toLowerCase().includes(searchLower) ||
        plan.repPlans?.some(rp =>
          rp.productid_Formatted?.toLowerCase().includes(searchLower) ||
          rp.segmentid_Formatted?.toLowerCase().includes(searchLower),
        ),
      );

      const formattedResult = matchedPlans.slice(0, limit).map(plan => ({
        id: plan.customerId || plan.contactId || plan.accountId,
        customerName: plan.customerName,
        customerType: plan.customerType,
        cyclePlanName: plan.cycleplanname,
        productName: plan.productName,
        completionPercentage: plan.percentage || 0,
        startDate: plan.indskr_startdate,
        endDate: plan.indskr_enddate,
        totalGoals: plan.definedGoals || 0,
        completedGoals: plan.completedGoals || 0,
      }));

      return {
        success: true,
        count: formattedResult.length,
        searchTerm: search_term,
        totalMatches: matchedPlans.length,
        callPlans: formattedResult,
      };
    } catch (error) {
      console.error('[SearchTools] Error searching call plans:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to search call plans' };
    }
  }

  @AITool('search_presentations', 'Search for sales presentations by name, brand, or specialty', [
    { name: 'search_term', type: 'string', description: 'The search term to find presentations', required: true },
    { name: 'limit', type: 'number', description: 'Maximum number of results to return', required: false, default: 10 },
  ])
  async searchPresentations(search_term: string, limit = 10): Promise<any> {
    try {
      const presentations = this.presentationService.presentation || [];

      if (presentations.length === 0) {
        return { success: true, message: 'No presentations available', presentations: [] };
      }

      const searchLower = search_term.toLowerCase();
      const matchedPresentations = presentations.filter(pres =>
        pres.name?.toLowerCase().includes(searchLower) ||
        (pres as any).description?.toLowerCase().includes(searchLower) ||
        pres.brands?.some((b: any) => b.name?.toLowerCase().includes(searchLower)) ||
        (pres as any).specialties?.some((s: any) => s.name?.toLowerCase().includes(searchLower)),
      );

      const formattedResult = matchedPresentations.slice(0, limit).map((pres: any) => ({
        id: pres.ioPresentationId || pres.contentId,
        name: pres.name,
        description: pres.description || '',
        brands: pres.brands?.map((b: any) => b.name).join(', ') || '',
        specialties: pres.specialties?.map((s: any) => s.name).join(', ') || '',
        language: pres.languageName || '',
        pageCount: pres.presentationPages?.length || 0,
        isDownloaded: pres.downloaded || false,
        isFavorite: pres.favourite || false,
        isNew: pres.isNew || false,
      }));

      return {
        success: true,
        count: formattedResult.length,
        searchTerm: search_term,
        presentations: formattedResult,
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to search presentations' };
    }
  }

  @AITool('search_resources', 'Search for resources/documents by name, type, or brand', [
    { name: 'search_term', type: 'string', description: 'The search term to find resources', required: true },
    { name: 'limit', type: 'number', description: 'Maximum number of results to return', required: false, default: 10 },
  ])
  async searchResources(search_term: string, limit = 10): Promise<any> {
    try {
      const resources = this.resourceService.allResources || [];

      if (resources.length === 0) {
        return { success: true, message: 'No resources available', resources: [] };
      }

      const searchLower = search_term.toLowerCase();
      const matchedResources = resources.filter((res: any) =>
        res.title?.toLowerCase().includes(searchLower) ||
        res.name?.toLowerCase().includes(searchLower) ||
        res.description?.toLowerCase().includes(searchLower) ||
        res.assetType?.toLowerCase().includes(searchLower) ||
        res.brands?.some((b: any) => b.name?.toLowerCase().includes(searchLower)),
      );

      const formattedResult = matchedResources.slice(0, limit).map((res: any) => ({
        id: res.ioResourceId || res.ioDocumentId,
        name: res.title || res.name,
        description: res.description || '',
        assetType: res.assetType || res.fileType || '',
        brands: res.brands?.map((b: any) => b.name).join(', ') || '',
        specialties: res.specialties?.map((s: any) => s.name).join(', ') || '',
        isDownloaded: res.downloaded || false,
      }));

      return {
        success: true,
        count: formattedResult.length,
        searchTerm: search_term,
        resources: formattedResult,
      };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to search resources' };
    }
  }
}
